/** GA4 event helpers. Respects consent mode already configured in index.html. */

export const EXPERIMENT_STORAGE_KEY = 'calensync-experiments';
const IDENTIFIED_USER_KEY = 'calensync-analytics-user';

export type AnalyticsParams = Record<string, string | number | boolean | undefined | null>;

type ExperimentAssignment = {
    experiment_id: string;
    variant: string;
};

function readActiveExperiment(): ExperimentAssignment | null {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        const raw = localStorage.getItem(EXPERIMENT_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as { experiment_id?: string; variant?: string };
        if (!parsed.experiment_id || !parsed.variant) {
            return null;
        }
        return { experiment_id: parsed.experiment_id, variant: parsed.variant };
    } catch {
        return null;
    }
}

function experimentParams(): AnalyticsParams {
    const assignment = readActiveExperiment();
    if (!assignment) {
        return {};
    }
    return {
        experiment_id: assignment.experiment_id,
        variant: assignment.variant,
    };
}

export function trackEvent(name: string, params?: AnalyticsParams): void {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
        return;
    }
    const payload: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries({ ...experimentParams(), ...params })) {
        if (value !== undefined && value !== null) {
            payload[key] = value;
        }
    }
    window.gtag('event', name, payload);
}

export function setAnalyticsUserId(userId: string | null): void {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
        return;
    }
    window.gtag('set', { user_id: userId ?? undefined });
}

/** Fire sign_up once for new accounts, login once per browser for returning users. */
export function trackAuthIdentify(user: { uuid: string; date_created: Date }): void {
    if (typeof window === 'undefined') {
        return;
    }
    setAnalyticsUserId(user.uuid);

    let previouslyIdentified: string | null = null;
    try {
        previouslyIdentified = localStorage.getItem(IDENTIFIED_USER_KEY);
    } catch {
        // ignore
    }

    if (previouslyIdentified === user.uuid) {
        return;
    }

    const createdMs = user.date_created.getTime();
    const isLikelyNew = Number.isFinite(createdMs) && Date.now() - createdMs < 10 * 60 * 1000;

    if (isLikelyNew) {
        trackEvent('sign_up', { method: 'oauth' });
    } else {
        trackEvent('login', { method: 'oauth' });
    }

    try {
        localStorage.setItem(IDENTIFIED_USER_KEY, user.uuid);
    } catch {
        // ignore
    }
}

export function trackCtaClick(page: string, location: string): void {
    trackEvent('cta_click', { page, location });
}

export function trackLoginClick(provider: string): void {
    trackEvent('login_click', { provider });
}

export function trackCalendarConnectClick(provider: string): void {
    trackEvent('calendar_connect_click', { provider });
}

export function trackCalendarConnected(provider: string): void {
    trackEvent('calendar_connected', { provider });
}

export function trackSyncRuleCreated(source: string, isFirst?: boolean): void {
    trackEvent('sync_rule_created', {
        source,
        ...(isFirst === undefined ? {} : { is_first: isFirst }),
    });
}

export function trackBeginCheckout(plan: string): void {
    trackEvent('begin_checkout', { plan });
}

export function trackPurchase(transactionId?: string): void {
    trackEvent('purchase', transactionId ? { transaction_id: transactionId } : undefined);
}

export function trackFeatureInterest(name: string): void {
    trackEvent('feature_interest', { name });
}
